import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, UserPlus, Pencil, Check, X, UserRound } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function TeamRow({ m, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: m.name, role: m.role, fun_fact: m.fun_fact });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/team/${m.id}`, form);
      toast.success("Team member updated.");
      setEditing(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${m.name} from the website?`)) return;
    try {
      await api.delete(`/admin/team/${m.id}`);
      toast.success("Team member removed.");
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="flex gap-3 rounded-2xl bg-white border border-stone-200 p-4" data-testid={`team-row-${m.id}`}>
      {m.image_url ? (
        <img src={`${BACKEND}${m.image_url}`} alt="" className="h-16 w-16 rounded-xl object-cover shrink-0" />
      ) : (
        <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-stone-100 text-stone-400 shrink-0">
          <UserRound className="h-8 w-8" />
        </span>
      )}
      {editing ? (
        <div className="flex-1 space-y-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} aria-label="Name" className="h-10 w-full rounded-lg border-2 border-stone-300 px-2 text-sm" data-testid={`team-edit-name-${m.id}`} />
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} aria-label="Role" className="h-10 w-full rounded-lg border-2 border-stone-300 px-2 text-sm" data-testid={`team-edit-role-${m.id}`} />
          <input value={form.fun_fact} onChange={(e) => setForm({ ...form, fun_fact: e.target.value })} aria-label="Fun fact" className="h-10 w-full rounded-lg border-2 border-stone-300 px-2 text-sm" data-testid={`team-edit-fact-${m.id}`} />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-orange-600 text-white text-sm font-semibold disabled:opacity-70" data-testid={`team-save-${m.id}`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
            </button>
            <button onClick={() => setEditing(false)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 border-stone-300 text-sm font-semibold">
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-stone-900">{m.name}</p>
            <p className="text-sm text-stone-600">{m.role}</p>
            <p className="text-sm text-stone-500 truncate">Fun fact: {m.fun_fact || "—"}</p>
          </div>
          <div className="flex gap-1 self-start">
            <button onClick={() => setEditing(true)} className="rounded-lg p-2 text-stone-500 hover:bg-orange-50 hover:text-orange-700" aria-label={`Edit ${m.name}`} data-testid={`team-edit-${m.id}`}>
              <Pencil className="h-5 w-5" />
            </button>
            <button onClick={remove} className="rounded-lg p-2 text-stone-500 hover:bg-red-50 hover:text-red-700" aria-label={`Remove ${m.name}`} data-testid={`team-delete-${m.id}`}>
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function TeamTab() {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", role: "", fun_fact: "" });
  const [file, setFile] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(() => {
    api.get("/team").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("role", form.role);
      fd.append("fun_fact", form.fun_fact);
      if (file) fd.append("image", file);
      await api.post("/admin/team", fd);
      toast.success("Team member added to the About page.");
      setForm({ name: "", role: "", fun_fact: "" });
      setFile(null);
      document.getElementById("team-photo-input").value = "";
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-2" data-testid="team-tab">
      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Add a team member</h3>
        <p className="mt-1 text-sm text-stone-500">Appears in "Meet the team" on the About page (flip cards with a fun fact).</p>
        <form onSubmit={add} className="mt-4 space-y-4 rounded-2xl bg-white border border-stone-200 p-5">
          <div>
            <label htmlFor="team-name" className="block text-base font-medium text-stone-900 mb-1">Name</label>
            <input id="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="team-name-input" />
          </div>
          <div>
            <label htmlFor="team-role" className="block text-base font-medium text-stone-900 mb-1">Role (e.g. Support Worker)</label>
            <input id="team-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} required className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="team-role-input" />
          </div>
          <div>
            <label htmlFor="team-fact" className="block text-base font-medium text-stone-900 mb-1">Fun fact (shows on the card flip)</label>
            <input id="team-fact" value={form.fun_fact} onChange={(e) => setForm({ ...form, fun_fact: e.target.value })} placeholder="e.g. Makes the best flapjacks in Salisbury" className="h-11 w-full rounded-xl border-2 border-stone-300 px-3 focus-visible:border-orange-600" data-testid="team-fact-input" />
          </div>
          <div>
            <label htmlFor="team-photo-input" className="block text-base font-medium text-stone-900 mb-1">Photo (optional)</label>
            <input id="team-photo-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={(e) => setFile(e.target.files[0] || null)} className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-orange-600 file:px-4 file:py-2 file:text-white file:font-semibold" data-testid="team-photo-file-input" />
          </div>
          <button type="submit" disabled={adding} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-600 text-white font-semibold hover:bg-orange-700 disabled:opacity-70" data-testid="team-add-button">
            {adding ? <Loader2 className="h-5 w-5 animate-spin" /> : <UserPlus className="h-5 w-5" />}
            Add team member
          </button>
        </form>
      </div>

      <div>
        <h3 className="font-heading text-lg font-semibold text-stone-900">Current team</h3>
        <div className="mt-4 space-y-3" data-testid="team-admin-list">
          {items === null ? (
            <Loader2 className="h-5 w-5 animate-spin text-stone-500" />
          ) : items.length === 0 ? (
            <p className="text-stone-600">No team members yet — the About page section stays hidden until you add someone.</p>
          ) : (
            items.map((m) => <TeamRow key={m.id} m={m} onChanged={load} />)
          )}
        </div>
      </div>
    </div>
  );
}
